import React from 'react'
import { Link } from 'react-router-dom'
import InvoiceGenerator from '../components/InvoiceGenerator'

const VATInvoicePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-semibold mb-4 backdrop-blur">
              <span>🌍</span>
              <span>UK • EU • Australia • Global</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              VAT Invoice Template (Tax Compliant)
            </h1>
            <p className="text-2xl text-blue-100 mb-8">
              Ensure compliance with tax laws. Our generator automatically includes VAT/GST breakdown and Tax ID fields required for legal billing.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg backdrop-blur">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>HMRC Compliant (UK)</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg backdrop-blur">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>EU VAT Directive</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg backdrop-blur">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>GST Ready (AU/NZ)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Warning Banner */}
      <div className="bg-yellow-50 border-y border-yellow-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <span className="text-3xl">⚠️</span>
            <div>
              <p className="text-lg text-gray-900 font-semibold mb-1">
                Non-compliant VAT invoices can result in penalties up to £3,000 (UK) or €5,000 (EU)
              </p>
              <p className="text-gray-700">
                Our generator ensures your invoices meet all legal requirements for VAT/GST billing in UK, EU, Australia, and other jurisdictions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Generator Widget */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Generate Your VAT Invoice Now</h2>
            <p className="text-lg text-gray-600">Automatic VAT calculations, tax breakdown, and compliant formatting included.</p>
          </div>
          <InvoiceGenerator />
        </div>

        {/* What is a VAT Invoice */}
        <div className="mb-16 bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            What is a VAT Invoice? (And Why It Matters)
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">📋 Definition</h3>
                <p className="text-gray-700">
                  A <strong>VAT invoice</strong> (Value Added Tax invoice) is a legally compliant document that shows the amount of VAT charged on goods or services. It's required for:
                </p>
                <ul className="space-y-2 text-gray-700 mt-4">
                  <li>• B2B transactions where buyer claims VAT back</li>
                  <li>• Sales over £250 (UK) or €1,000 (EU)</li>
                  <li>• Export documentation for customs</li>
                  <li>• Tax authority audits and compliance</li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">⚖️ Legal Requirements</h3>
                <p className="text-gray-700">
                  To be legally valid, a VAT invoice <strong>must include</strong>:
                </p>
                <ul className="space-y-2 text-gray-700 mt-4">
                  <li>✓ Your VAT registration number</li>
                  <li>✓ Customer's VAT number (if VAT registered)</li>
                  <li>✓ Unique sequential invoice number</li>
                  <li>✓ Tax rate applied (e.g., 20% Standard Rate)</li>
                  <li>✓ Amount before VAT (net amount)</li>
                  <li>✓ VAT amount charged</li>
                  <li>✓ Total amount including VAT (gross)</li>
                </ul>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
              <p className="text-gray-900 font-semibold mb-2">❌ Missing any of these? Your invoice is not legally compliant.</p>
              <p className="text-gray-700">
                Tax authorities can reject invoices, disallow VAT reclaims, and impose penalties if your invoices don't meet requirements. Our generator ensures every invoice includes all mandatory fields.
              </p>
            </div>
          </div>
        </div>

        {/* VAT Rates by Country */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Common VAT/GST Rates Supported
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { country: '🇬🇧 United Kingdom', standard: '20%', reduced: '5%', zero: '0%' },
              { country: '🇩🇪 Germany', standard: '19%', reduced: '7%', zero: '0%' },
              { country: '🇫🇷 France', standard: '20%', reduced: '5.5%', zero: '0%' },
              { country: '🇮🇹 Italy', standard: '22%', reduced: '10%', zero: '0%' },
              { country: '🇪🇸 Spain', standard: '21%', reduced: '10%', zero: '0%' },
              { country: '🇦🇺 Australia (GST)', standard: '10%', reduced: 'N/A', zero: '0%' },
              { country: '🇨🇦 Canada (GST/HST)', standard: '5-15%', reduced: 'Varies', zero: '0%' },
              { country: '🇳🇿 New Zealand (GST)', standard: '15%', reduced: 'N/A', zero: '0%' },
              { country: '🇸🇬 Singapore (GST)', standard: '9%', reduced: 'N/A', zero: '0%' }
            ].map((rate, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-blue-600">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{rate.country}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Standard Rate:</span>
                    <span className="font-bold text-gray-900">{rate.standard}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reduced Rate:</span>
                    <span className="font-bold text-gray-900">{rate.reduced}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Zero Rate:</span>
                    <span className="font-bold text-gray-900">{rate.zero}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-6">
            <p className="text-gray-900">
              <strong>✓ Our tool supports custom VAT rates.</strong> Enter any percentage (5%, 10%, 19%, 20%, 21%, 23%, etc.) and we'll calculate the breakdown automatically.
            </p>
          </div>
        </div>

        {/* Features for VAT Compliance */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            How Our Generator Ensures VAT Compliance
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: '🔢',
                title: 'Automatic VAT Calculation',
                description: 'Enter your net amount and VAT rate (e.g., 20%). We automatically calculate the VAT amount and gross total. No manual math errors.',
                compliance: 'Prevents calculation mistakes that trigger audits'
              },
              {
                icon: '📋',
                title: 'Mandatory Fields Enforced',
                description: 'Can\'t generate invoice without VAT number, tax rate, and sequential invoice number. Ensures every invoice is compliant from the start.',
                compliance: 'Meets HMRC, EU VAT Directive, and ATO requirements'
              },
              {
                icon: '🔍',
                title: 'Clear VAT Breakdown',
                description: 'Shows subtotal, VAT amount, and total separately. Clients can easily verify charges and reclaim VAT from tax authorities.',
                compliance: 'Required format for B2B VAT reclaims'
              },
              {
                icon: '🌍',
                title: 'Reverse Charge Mechanism',
                description: 'For EU cross-border B2B sales, mark invoice as "Reverse Charge" to indicate customer must account for VAT.',
                compliance: 'EU VAT reverse charge compliance'
              },
              {
                icon: '💼',
                title: 'VAT Number Validation',
                description: 'Prompts for both your VAT registration number and customer\'s VAT number (for B2B). Ensures proper documentation.',
                compliance: 'Essential for EU VIES validation'
              },
              {
                icon: '📄',
                title: 'Sequential Numbering',
                description: 'Automatically generates unique invoice numbers in sequence (INV-001, INV-002, etc.). Required by tax authorities worldwide.',
                compliance: 'HMRC and EU mandatory requirement'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-600">
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-4xl">{feature.icon}</div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-700 mb-3">{feature.description}</p>
                    <div className="bg-green-50 px-3 py-2 rounded-lg">
                      <p className="text-sm text-green-700 font-semibold">✓ {feature.compliance}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* VAT Invoice Example */}
        <div className="mb-16 bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Example: Compliant VAT Invoice Breakdown
          </h2>
          
          <div className="max-w-2xl mx-auto bg-gray-50 rounded-lg p-8 border-2 border-gray-300 font-mono text-sm">
            <div className="space-y-4">
              <div>
                <p className="font-bold text-gray-900 mb-2">INVOICE #INV-2024-001</p>
                <p className="text-gray-700">Date: 10 February 2024</p>
              </div>

              <div className="border-t border-gray-300 pt-4">
                <p className="font-bold text-gray-900 mb-2">From:</p>
                <p className="text-gray-700">Your Company Ltd</p>
                <p className="text-gray-700">VAT Registration: GB123456789</p>
              </div>

              <div className="border-t border-gray-300 pt-4">
                <p className="font-bold text-gray-900 mb-2">To:</p>
                <p className="text-gray-700">Client Business Ltd</p>
                <p className="text-gray-700">VAT Registration: GB987654321</p>
              </div>

              <div className="border-t border-gray-300 pt-4">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2">Description</th>
                      <th className="text-right py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 text-gray-700">Consulting Services</td>
                      <td className="text-right text-gray-700">£1,000.00</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="border-t border-gray-300 pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-700">Subtotal (Net):</span>
                  <span className="text-gray-900 font-bold">£1,000.00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">VAT @ 20%:</span>
                  <span className="text-gray-900 font-bold">£200.00</span>
                </div>
                <div className="flex justify-between text-xl border-t-2 border-gray-900 pt-2">
                  <span className="font-bold text-gray-900">Total (Gross):</span>
                  <span className="font-bold text-gray-900">£1,200.00</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-600">
              <h3 className="font-bold text-gray-900 mb-3">✅ What Makes This Compliant:</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• Sequential invoice number (INV-2024-001)</li>
                <li>• Both VAT numbers included</li>
                <li>• Clear net/VAT/gross breakdown</li>
                <li>• VAT rate specified (20%)</li>
                <li>• Professional formatting</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
              <h3 className="font-bold text-gray-900 mb-3">❌ Common Non-Compliant Mistakes:</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• Missing VAT registration numbers</li>
                <li>• No VAT breakdown shown</li>
                <li>• Only showing total (not net + VAT)</li>
                <li>• Generic invoice numbers (Invoice #1)</li>
                <li>• VAT rate not specified</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Who Needs VAT Invoices */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Who Needs VAT/GST Invoices?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'UK Businesses',
                description: 'If you\'re VAT-registered in the UK (turnover > £85,000), you must issue VAT invoices for all B2B sales.',
                requirement: 'HMRC Mandatory'
              },
              {
                title: 'EU Traders',
                description: 'EU businesses selling goods/services above VAT threshold must issue compliant VAT invoices under EU VAT Directive.',
                requirement: 'EU Law Required'
              },
              {
                title: 'Australian Businesses',
                description: 'If GST-registered in Australia (turnover > $75,000), you must provide tax invoices for sales over $82.50.',
                requirement: 'ATO Mandatory'
              },
              {
                title: 'Exporters',
                description: 'Zero-rated exports require VAT invoices showing 0% rate for customs documentation and client VAT reclaims.',
                requirement: 'Export Evidence'
              },
              {
                title: 'B2B Service Providers',
                description: 'Consultants, agencies, and contractors selling to VAT-registered businesses need compliant invoices.',
                requirement: 'Client Requirement'
              },
              {
                title: 'eCommerce Sellers',
                description: 'Online sellers shipping to EU/UK/AU need VAT invoices for orders above threshold and B2B transactions.',
                requirement: 'Platform Compliance'
              }
            ].map((group, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-blue-600">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{group.title}</h3>
                <p className="text-gray-700 mb-4">{group.description}</p>
                <div className="bg-blue-50 px-3 py-2 rounded-lg">
                  <p className="text-sm text-blue-700 font-semibold">📋 {group.requirement}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            VAT Invoice FAQs
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                q: 'What\'s the difference between a VAT invoice and a regular invoice?',
                a: 'A VAT invoice includes specific tax information (VAT registration numbers, tax rate, VAT amount breakdown) required by law. Regular invoices may not meet legal compliance requirements for VAT-registered transactions.'
              },
              {
                q: 'Do I need a VAT invoice for all sales?',
                a: 'Only if you\'re VAT-registered. Small businesses below the VAT threshold (£85k UK, varies by country) don\'t need to charge VAT or issue VAT invoices. But if you are registered, it\'s mandatory.'
              },
              {
                q: 'Can I handwrite a VAT invoice?',
                a: 'Technically yes, but it\'s risky. Handwritten invoices are prone to errors and may not include all required fields. Digital generation ensures compliance every time.'
              },
              {
                q: 'What happens if my VAT invoice is wrong?',
                a: 'Incorrect VAT invoices can be rejected by tax authorities. Clients may be unable to reclaim VAT, you could face penalties (£3,000+ in UK), and audits become more likely. Always use compliant formatting.'
              },
              {
                q: 'Do I need my customer\'s VAT number?',
                a: 'For B2B sales to VAT-registered businesses, yes. It proves they can reclaim the VAT and is required for EU cross-border sales. For B2C (selling to consumers), it\'s not needed.'
              },
              {
                q: 'Is this tool compliant with my country\'s VAT laws?',
                a: 'Our generator follows HMRC (UK), EU VAT Directive, ATO (Australia), and international tax standards. It includes all mandatory fields required for legal VAT invoices in major jurisdictions.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-600">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl p-8 md:p-12 text-center text-white shadow-2xl">
          <h2 className="text-4xl font-bold mb-4">Generate Tax-Compliant VAT Invoices Now</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Stop worrying about VAT compliance. Create legally valid VAT invoices with automatic calculations and all required fields—completely free.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-blue-600 px-10 py-5 rounded-lg font-bold text-xl hover:bg-blue-50 transition-colors shadow-lg"
          >
            Create VAT Invoice Now
          </Link>
          <p className="mt-4 text-sm text-blue-100">HMRC • EU • ATO compliant • Automatic VAT breakdown • Free forever</p>
        </div>
      </div>
    </div>
  )
}

export default VATInvoicePage
